import {useInfiniteQuery, useQuery} from '@tanstack/react-query';
import {fetchOffer, fetchOffers, fetchMonthlyPerksSummary, fetchOfferBookmarkedUsers} from '../endpoints/api';

interface FetchOffersOptions {
  page?: string;
  articleType?: 'partnerId' | 'placeId' | 'eventId' | 'postId';
  articleId?: string | null;
  enabled?: boolean;
  communityId?: string;
  onlyIndividualRedemptions?: boolean;
}

export const useFetchOffer = (articleId: string | null) => {
  return useQuery(['offer', {articleId}], () => fetchOffer(Number(articleId)), {
    enabled: !!articleId,
  });
};

export const useMonthlyPerksSummary = () => {
  return useQuery(['monthly-perks-summary'], fetchMonthlyPerksSummary, {
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};

export const useFetchOffers = ({
  page = '1',
  articleType,
  articleId,
  enabled = true,
  communityId,
  onlyIndividualRedemptions,
}: FetchOffersOptions) => {
  return useQuery(
    [
      'offers',
      {
        page,
        ...(articleType && {articleType}),
        ...(articleId && {articleId}),
        ...(communityId && {communityId}),
        ...(onlyIndividualRedemptions && {onlyIndividualRedemptions}),
      },
    ],
    () =>
      fetchOffers({
        page,
        ...(articleType && {articleType}),
        ...(articleId && {articleId}),
        ...(communityId && {communityId}),
        ...(onlyIndividualRedemptions && {onlyIndividualRedemptions}),
      }),
    {enabled: enabled}
  );
};

export const useInfiniteOffers = ({articleType, articleId, communityId}) => {
  return useInfiniteQuery({
    queryKey: ['infinite-offers', {articleType, articleId, communityId}],
    queryFn: (params) =>
      fetchOffers({
        page: params.pageParam || 1,
        ...(articleType && {articleType}),
        ...(articleId && {articleId}),
        ...(communityId && {communityId}),
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.data.current_page !== lastPage.data.last_page) {
        return lastPage.data.current_page + 1;
      }
    },
    // keepPreviousData: true,
  });
};

export const useOffers = ({
  page,
  articleType,
  articleId,
  enabled,
  communityId,
  onlyIndividualRedemptions,
}: FetchOffersOptions) => {
  const fetchOffers = useFetchOffers({
    page,
    articleType,
    articleId,
    enabled,
    communityId,
    onlyIndividualRedemptions,
  });

  return {offers: fetchOffers.data?.data.data, offersIsLoading: fetchOffers.isLoading};
};

export function useFetchOfferBookmarkedUsers(offerId: number, enabled: boolean = true) {
  return useInfiniteQuery(
    ['offerBookmarkedUsers', offerId],
    ({pageParam = 1}) => fetchOfferBookmarkedUsers(offerId, pageParam),
    {
      enabled: enabled && !!offerId,
      getNextPageParam: (lastPage) => {
        const {current_page, last_page} = lastPage.data;
        return current_page < last_page ? current_page + 1 : undefined;
      },
    }
  );
}
